/**
 * Matchmaking Store - Zustand
 * Manages PvP queue status, gate locking, selected stage, AI fallback
 */

import { create } from 'zustand';
import type { QueueStage, AIDifficulty, MatchFoundPayload } from '../types/socket';
import { GATE_THRESHOLDS } from '../types';

/**
 * Matchmaking store state
 */
export interface MatchmakingState {
  // Queue state
  isQueued: boolean;
  queueStage: QueueStage | null;
  queueMessage: string | null;
  queueJoinedAt: number | null;
  
  // Gate locking (PvP < 200, Hard AI < 700)
  pvpGateLocked: boolean;
  pvpPointsNeeded: number;
  hardAiGateLocked: boolean;
  hardAiPointsNeeded: number;
  userScore: number;
  userStage: number;
  
  // AI fallback
  aiFallbackTriggered: boolean;
  aiDifficulty: AIDifficulty | null;
  aiMatchFound: MatchFoundPayload | null;
  
  // Match found (PvP or AI)
  matchFound: MatchFoundPayload | null;
  
  // Actions
  setQueued: (queued: boolean, stage?: QueueStage, message?: string) => void;
  setQueueLeft: (message: string) => void;
  setGateStatus: (pvpLocked: boolean, pvpPointsNeeded: number, hardAiLocked: boolean, hardAiPointsNeeded: number, userScore: number, userStage: number) => void;
  setMatchFound: (match: MatchFoundPayload | null) => void;
  setAiFallback: (triggered: boolean, difficulty?: AIDifficulty, match?: MatchFoundPayload | null) => void;
  clearAiFallback: () => void;
  clearMatchFound: () => void;
  reset: () => void;
}

/**
 * Initial state
 */
const initialState: Omit<MatchmakingState, keyof {
  setQueued: any; setQueueLeft: any; setGateStatus: any; setMatchFound: any;
  setAiFallback: any; clearAiFallback: any; clearMatchFound: any; reset: any;
}> = {
  isQueued: false,
  queueStage: null,
  queueMessage: null,
  queueJoinedAt: null,
  pvpGateLocked: false,
  pvpPointsNeeded: 0,
  hardAiGateLocked: false,
  hardAiPointsNeeded: 0,
  userScore: 0,
  userStage: 1,
  aiFallbackTriggered: false,
  aiDifficulty: null,
  aiMatchFound: null,
  matchFound: null,
};

/**
 * Matchmaking store
 */
export const useMatchmakingStore = create<MatchmakingState>((set) => ({
  ...initialState,
  
  setQueued: (queued: boolean, stage?: QueueStage, message?: string) => 
    set((state) => ({
      isQueued: queued,
      queueStage: queued ? (stage ?? state.queueStage) : null,
      queueMessage: message ?? (queued ? 'Searching for opponent...' : null),
      queueJoinedAt: queued ? Date.now() : null,
      // Clear previous match found when joining queue
      matchFound: queued ? null : state.matchFound,
    })),
  
  setQueueLeft: (message: string) => 
    set({
      isQueued: false,
      queueStage: null,
      queueMessage: message,
      queueJoinedAt: null,
    }),
  
  setGateStatus: (
    pvpLocked: boolean,
    pvpPointsNeeded: number,
    hardAiLocked: boolean,
    hardAiPointsNeeded: number,
    userScore: number,
    userStage: number
  ) => set({
    pvpGateLocked: pvpLocked,
    pvpPointsNeeded,
    hardAiGateLocked: hardAiLocked,
    hardAiPointsNeeded,
    userScore,
    userStage,
  }),
  
  setMatchFound: (match: MatchFoundPayload | null) => 
    set({
      matchFound: match,
      isQueued: false,
      queueStage: null,
      queueMessage: null,
      queueJoinedAt: null,
    }),
  
  setAiFallback: (triggered: boolean, difficulty?: AIDifficulty, match?: MatchFoundPayload | null) => 
    set({
      aiFallbackTriggered: triggered,
      aiDifficulty: difficulty ?? null,
      aiMatchFound: match ?? null,
      isQueued: false,
      queueStage: null,
      queueMessage: null,
      queueJoinedAt: null,
    }),
  
  clearAiFallback: () => 
    set({
      aiFallbackTriggered: false,
      aiDifficulty: null,
      aiMatchFound: null,
    }),
  
  clearMatchFound: () => 
    set({
      matchFound: null,
    }),
  
  reset: () => set(initialState),
}));

/**
 * Selectors
 */

// Check if PvP gate is locked for current user
export const useIsPvpGateLocked = () => 
  useMatchmakingStore((state) => state.pvpGateLocked);

// Check if Hard AI gate is locked
export const useIsHardAiGateLocked = () => 
  useMatchmakingStore((state) => state.hardAiGateLocked);

// Get points needed for PvP
export const usePvpPointsNeeded = () => 
  useMatchmakingStore((state) => state.pvpPointsNeeded);

// Get points needed for Hard AI
export const useHardAiPointsNeeded = () => 
  useMatchmakingStore((state) => state.hardAiPointsNeeded);

// Get user score and stage
export const useUserScore = () => 
  useMatchmakingStore((state) => ({ score: state.userScore, stage: state.userStage }));

// Check if currently queued
export const useIsQueued = () => 
  useMatchmakingStore((state) => state.isQueued);

// Get queue stage
export const useQueueStage = () => 
  useMatchmakingStore((state) => state.queueStage);

// Get queue message
export const useQueueMessage = () => 
  useMatchmakingStore((state) => state.queueMessage);

// Get match found
export const useMatchFound = () => 
  useMatchmakingStore((state) => state.matchFound);

// Check if AI fallback was triggered
export const useAiFallbackTriggered = () => 
  useMatchmakingStore((state) => state.aiFallbackTriggered);

// Get AI match found
export const useAiMatchFound = () => 
  useMatchmakingStore((state) => state.aiMatchFound);

// Combined matchmaking state
export const useMatchmaking = () => {
  const isQueued = useMatchmakingStore((state) => state.isQueued);
  const queueStage = useMatchmakingStore((state) => state.queueStage);
  const queueMessage = useMatchmakingStore((state) => state.queueMessage);
  const queueJoinedAt = useMatchmakingStore((state) => state.queueJoinedAt);
  const pvpGateLocked = useMatchmakingStore((state) => state.pvpGateLocked);
  const pvpPointsNeeded = useMatchmakingStore((state) => state.pvpPointsNeeded);
  const hardAiGateLocked = useMatchmakingStore((state) => state.hardAiGateLocked);
  const hardAiPointsNeeded = useMatchmakingStore((state) => state.hardAiPointsNeeded);
  const userScore = useMatchmakingStore((state) => state.userScore);
  const userStage = useMatchmakingStore((state) => state.userStage);
  const aiFallbackTriggered = useMatchmakingStore((state) => state.aiFallbackTriggered);
  const aiDifficulty = useMatchmakingStore((state) => state.aiDifficulty);
  const aiMatchFound = useMatchmakingStore((state) => state.aiMatchFound);
  const matchFound = useMatchmakingStore((state) => state.matchFound);
  
  const setQueued = useMatchmakingStore((state) => state.setQueued);
  const setQueueLeft = useMatchmakingStore((state) => state.setQueueLeft);
  const setGateStatus = useMatchmakingStore((state) => state.setGateStatus);
  const setMatchFound = useMatchmakingStore((state) => state.setMatchFound);
  const setAiFallback = useMatchmakingStore((state) => state.setAiFallback);
  const clearAiFallback = useMatchmakingStore((state) => state.clearAiFallback);
  const clearMatchFound = useMatchmakingStore((state) => state.clearMatchFound);
  const reset = useMatchmakingStore((state) => state.reset);
  
  return {
    isQueued,
    queueStage,
    queueMessage,
    queueJoinedAt,
    pvpGateLocked,
    pvpPointsNeeded,
    hardAiGateLocked,
    hardAiPointsNeeded,
    userScore,
    userStage,
    aiFallbackTriggered,
    aiDifficulty,
    aiMatchFound,
    matchFound,
    setQueued,
    setQueueLeft,
    setGateStatus,
    setMatchFound,
    setAiFallback,
    clearAiFallback,
    clearMatchFound,
    reset,
  };
};

/**
 * Calculate gate status from user score
 * Can be used to update store from auth/profile data
 */
export function calculateGateStatus(score: number): {
  pvpLocked: boolean;
  pvpPointsNeeded: number;
  hardAiLocked: boolean;
  hardAiPointsNeeded: number;
  stage: number;
} {
  const pvpLocked = score < GATE_THRESHOLDS.PVP;
  const hardAiLocked = score < GATE_THRESHOLDS.HARD_AI;
  
  let stage = 1;
  if (score >= 2000) stage = 5;
  else if (score >= 1500) stage = 4;
  else if (score >= 1000) stage = 3;
  else if (score >= 500) stage = 2;
  
  return {
    pvpLocked,
    pvpPointsNeeded: Math.max(0, GATE_THRESHOLDS.PVP - score),
    hardAiLocked,
    hardAiPointsNeeded: Math.max(0, GATE_THRESHOLDS.HARD_AI - score),
    stage,
  };
}

/**
 * Stage display names
 */
export const STAGE_NAMES: Record<number, string> = {
  1: 'Stage 1 (0-499)',
  2: 'Stage 2 (500-999)',
  3: 'Stage 3 (1000-1499)',
  4: 'Stage 4 (1500-1999)',
  5: 'Stage 5 (2000+)',
} as const;

/**
 * AI difficulty for each stage
 */
export const STAGE_AI_DIFFICULTY: Record<number, AIDifficulty> = {
  1: 'easy',
  2: 'medium',
  3: 'hard',
  4: 'hard',
  5: 'hard',
} as const;